// The Cockpit's pages and sidebar groups (docs/cockpit/spec.md), named by their keys in the catalogues
// (i18n/en.ts). The pages in BUILT are built; every other page is a placeholder for now. Set up LAMP isn't in
// the spec's list: it is the Owner's onboarding, under Setup.
import type { Words } from "../i18n/words.js";
import type { IconName } from "./icons.js";

/** A catalogue key whose entry is plain words. */
export type WordKey = { [K in keyof Words]: Words[K] extends string ? K : never }[keyof Words];

export const PAGE_IDS = [
  "today",
  "chat",
  "floor",
  "runs",
  "documents",
  "parties",
  "items",
  "cash",
  "bills",
  "expenses",
  "gst",
  "tree",
  "rules",
  "templates",
  "schedules",
  "memory",
  "quality",
  "insights",
  "credits",
  "audit",
  "onboarding",
  "users",
  "roles",
  "channels",
  "connections",
  "models",
  "settings",
  "billing",
  "security",
  "help",
  "status",
] as const;
export type Page = (typeof PAGE_IDS)[number];

export const PAGES: Readonly<Record<Page, { readonly path: string; readonly title: WordKey }>> = {
  today: { path: "/", title: "pageToday" },
  chat: { path: "/chat", title: "pageChat" },
  floor: { path: "/floor", title: "pageFloor" },
  runs: { path: "/runs", title: "pageRuns" },
  documents: { path: "/documents", title: "pageDocuments" },
  parties: { path: "/parties", title: "pageParties" },
  items: { path: "/items", title: "pageItems" },
  cash: { path: "/cash", title: "pageCash" },
  bills: { path: "/bills", title: "pageBills" },
  expenses: { path: "/expenses", title: "pageExpenses" },
  gst: { path: "/gst", title: "pageGst" },
  tree: { path: "/tree", title: "pageTree" },
  rules: { path: "/rules", title: "pageRules" },
  templates: { path: "/templates", title: "pageTemplates" },
  schedules: { path: "/schedules", title: "pageSchedules" },
  memory: { path: "/memory", title: "pageMemory" },
  quality: { path: "/quality", title: "pageQuality" },
  insights: { path: "/insights", title: "pageInsights" },
  credits: { path: "/credits", title: "pageCredits" },
  // Not `/audit`: the api answers that path with the Audit Log's entries.
  audit: { path: "/audit-log", title: "pageAudit" },
  onboarding: { path: "/set-up", title: "pageOnboarding" },
  users: { path: "/users", title: "pageUsers" },
  roles: { path: "/roles", title: "pageRoles" },
  channels: { path: "/channels", title: "pageChannels" },
  connections: { path: "/connections", title: "pageConnections" },
  models: { path: "/models", title: "pageModels" },
  settings: { path: "/settings", title: "pageSettings" },
  billing: { path: "/billing", title: "pageBilling" },
  security: { path: "/security", title: "pageSecurity" },
  help: { path: "/help", title: "pageHelp" },
  status: { path: "/status", title: "pageStatus" },
};

/** The pages built so far; the rest show a placeholder. */
export const BUILT: ReadonlySet<Page> = new Set<Page>([
  "today",
  "chat",
  "floor",
  "runs",
  "documents",
  "parties",
  "items",
  "cash",
  "gst",
  "credits",
  "memory",
  "insights",
  "quality",
  "bills",
  "expenses",
  "audit",
  "onboarding",
  "users",
  "roles",
  "connections",
  "settings",
  "billing",
  "models",
  "security",
  "help",
  "status",
]);
/** Pages only an Owner sees: the Application API refuses them to anyone else. */
export const OWNER_PAGES: ReadonlySet<Page> = new Set<Page>([
  "memory",
  "onboarding",
  "credits",
  "audit",
  "users",
  "roles",
  "billing",
]);

/**
 * Whether the sidebar lists `page` (#513): one of the pages the Application API says the person's Roles open (`opens`,
 * from `GET /api/pages`, by the same authorize() answers the pages' routes give), and never an Owner's page to anyone
 * else. Until those are known, every page but the Owner's is listed, as before.
 */
export function listed(page: Page, owner: boolean, opens: ReadonlySet<string> | undefined): boolean {
  if (OWNER_PAGES.has(page) && !owner) return false;
  return opens === undefined || opens.has(page);
}

/**
 * Whether the person may open `page`, typed or linked to (#989): one of the pages the Application API says they may open
 * (`opens`, from `GET /api/pages`), which a page with nothing to show yet still is, and never an Owner's page to anyone
 * else. Until those are known, every page but the Owner's opens.
 */
export function mayOpen(page: Page, owner: boolean, opens: ReadonlySet<string> | undefined): boolean {
  if (OWNER_PAGES.has(page) && !owner) return false;
  return opens === undefined || opens.has(page);
}

export interface NavGroup {
  readonly id: string;
  readonly label: WordKey;
  readonly pages: readonly Page[];
}

export const NAV: readonly NavGroup[] = [
  {
    id: "run",
    label: "navRun",
    pages: ["today", "chat", "floor", "runs", "documents", "parties", "items"],
  },
  { id: "money", label: "navMoney", pages: ["cash", "bills", "expenses", "gst"] },
  {
    id: "how",
    label: "navHow",
    pages: ["tree", "rules", "templates", "schedules", "memory"],
  },
  { id: "watch", label: "navWatch", pages: ["quality", "insights", "credits", "audit"] },
  {
    id: "setup",
    label: "navSetup",
    pages: [
      "onboarding",
      "users",
      "roles",
      "channels",
      "connections",
      "models",
      "settings",
      "billing",
      "security",
      "help",
      "status",
    ],
  },
];

/** The phone's bottom tabs; the fifth, Menu, opens the sidebar as a drawer. */
export const TABS: readonly { readonly page: Page; readonly label: WordKey; readonly icon: IconName }[] = [
  { page: "today", label: "tabToday", icon: "today" },
  { page: "floor", label: "tabFloor", icon: "floor" },
  { page: "chat", label: "tabChat", icon: "chat" },
  { page: "documents", label: "tabDocuments", icon: "documents" },
];

/** The page a path shows: its first segment, so `/runs/…` is Runs. Unknown paths show Today. */
export function pageAt(path: string): Page {
  const first = `/${path.split("/")[1] ?? ""}`;
  return PAGE_IDS.find((p) => PAGES[p].path === first) ?? "today";
}
